import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, DollarSign, Calendar, Percent, Clock, Shield, TrendingUp, Users, BookOpen, MessageCircle } from 'lucide-react';

const UnsecuredLoans = () => {
  const fundingDetails = [
    {
      icon: <DollarSign className="w-12 h-12 text-accent-500" />,
      title: "Funding Amount",
      value: "£1,000 – £500,000",
      description: "Unsecured loans can provide funding for a wide range of business needs depending on lender and financial profile",
    },
    {
      icon: <Calendar className="w-12 h-12 text-accent-500" />,
      title: "Loan Period",
      value: "1 – 5 Years",
      description: "Unsecured business loans are usually structured with fixed monthly repayments over a period of one to five years",
    },
    {
      icon: <Percent className="w-12 h-12 text-accent-500" />,
      title: "Typical Cost",
      value: "7% – 20% APR",
      description: "Interest rates vary depending on credit profile, loan amount, and business performance",
    },
  ];

  const features = [
    {
      icon: <Clock className="w-8 h-8 text-primary-600" />,
      title: "Minimum Trading History",
      description: "Businesses often need to be trading for at least two years to qualify for unsecured business funding",
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-primary-600" />,
      title: "Good Credit Profile",
      description: "Many lenders review the credit history of the business and its directors before approving unsecured loans",
    },
    {
      icon: <Shield className="w-8 h-8 text-primary-600" />,
      title: "Affordable Repayments",
      description: "Loans are typically repaid through fixed monthly payments agreed with the lender",
    },
    {
      icon: <Users className="w-8 h-8 text-primary-600" />,
      title: "Personal Guarantees",
      description: "Some lenders may require a director to provide a personal guarantee as part of the loan agreement",
    },
  ];

  const useCases = [
    "Hiring new employees",
    "Expanding into new markets",
    "Purchasing equipment",
    "Managing working capital",
    "Funding marketing campaigns",
  ];

  const resources = [
    {
      icon: <BookOpen className="w-8 h-8 text-primary-600" />,
      title: "SME Funding Guide",
      description: "Learn about different funding solutions available to small and medium-sized businesses",
      button: "Read More",
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-primary-600" />,
      title: "Case Studies",
      description: "Discover how 1ST TRUST FINANCES has helped businesses secure the funding they need",
      button: "View Case Studies",
    },
    {
      icon: <MessageCircle className="w-8 h-8 text-primary-600" />,
      title: "Speak to a Funding Specialist",
      description: "Our experts can help you understand whether unsecured business loans are right for your business",
      button: "Contact Us",
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-900 leading-tight">
                What is an{' '}
                <span className="gradient-text">Unsecured Business Loan</span>?
              </h1>
              <p className="text-xl text-gray-600 font-medium">
                Access business funding without providing assets as collateral.
              </p>
              <p className="text-gray-600 leading-relaxed">
                An unsecured business loan allows businesses to borrow money without using property, equipment, or other assets as security.
              </p>
              <p className="text-gray-600 leading-relaxed">
                At 1ST TRUST FINANCES, we help businesses access flexible unsecured loan solutions that provide funding quickly while allowing business owners to retain full control of their assets.
              </p>
              <p className="text-gray-600 leading-relaxed">
                These loans are commonly used by growing businesses looking to invest in expansion, staff, or working capital.
              </p>
              <Link to="/apply-for-funding" className="btn-primary inline-flex items-center">
                Apply for Funding
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="unsecuredGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#unsecuredGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <rect x="50" y="50" width="200" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="100" height="20" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="120" width="140" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="150" width="120" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="190" width="160" height="40" rx="5" fill="#0056d6" />
                  <Shield className="absolute" x="180" y="60" width="50" height="50" fill="#0ea5e9" opacity="0.3" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is an Unsecured Business Loan */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="whatIsUnsecuredGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#whatIsUnsecuredGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <circle cx="150" cy="100" r="50" fill="#0056d6" opacity="0.2" />
                  <circle cx="80" cy="150" r="35" fill="#0ea5e9" opacity="0.2" />
                  <circle cx="220" cy="150" r="35" fill="#0056d6" opacity="0.15" />
                  <rect x="50" y="200" width="200" height="80" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="230" width="100" height="20" rx="5" fill="#e6f0ff" />
                </g>
              </svg>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Business Funding Without Asset Security
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Unlike secured loans, unsecured business loans do not require businesses to provide assets as collateral.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Lenders evaluate the business based on financial performance, credit history, and the ability to repay the loan.
              </p>
              <p className="text-gray-600 leading-relaxed">
                These loans are offered by banks as well as alternative lenders and are typically suitable for established businesses with consistent revenue and profitability.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Funding Details Section */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Loan Details
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Clear and transparent funding terms for your business.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {fundingDetails.map((detail, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-6">{detail.icon}</div>
                <h3 className="font-heading text-xl font-bold text-primary-900 mb-2">
                  {detail.title}
                </h3>
                <p className="text-2xl font-bold text-accent-600 mb-4">
                  {detail.value}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  {detail.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Unsecured Business Loan At a Glance */}
      <section className="section bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Unsecured Business Loan at a Glance
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Key features and requirements of unsecured business loans.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="font-heading text-lg font-bold text-primary-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How Businesses Use Unsecured Loans */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Common Uses for Unsecured Business Loans
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Businesses use unsecured loans for a wide range of operational and growth needs.
              </p>
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h4 className="font-semibold text-primary-900 mb-4">Common uses include:</h4>
                <ul className="space-y-3">
                  {useCases.map((useCase, index) => (
                    <li key={index} className="flex items-start text-gray-600">
                      <ArrowRight className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" />
                      {useCase}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Because unsecured loans do not require assets as security, they can be a flexible option for businesses that want quick access to capital.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="usesGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#usesGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <rect x="50" y="50" width="200" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="80" height="20" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="120" width="140" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="150" width="120" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="180" width="100" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="210" width="160" height="30" rx="5" fill="#0056d6" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Resources
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Helpful resources to guide your funding journey.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {resources.map((resource, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-6">{resource.icon}</div>
                <h3 className="font-heading text-xl font-bold text-primary-900 mb-3">
                  {resource.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {resource.description}
                </p>
                <button className="btn-secondary w-full">
                  {resource.button}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section gradient-bg text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg viewBox="0 0 1000 1000" className="w-full h-full">
            <defs>
              <pattern id="grid4" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid4)" />
          </svg>
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Get Unsecured Business Funding Today
            </h2>
            <p className="text-xl text-gray-300 mb-4">
              1ST TRUST FINANCES connects businesses with lenders offering flexible unsecured loan solutions.
            </p>
            <p className="text-gray-300 mb-8">
              Receive funding offers quickly and choose the option that works best for your business.
            </p>
            <Link to="/apply-for-funding" className="inline-block bg-white text-primary-600 font-semibold px-8 py-4 rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
              Get Funding Quotes
              <ArrowRight className="inline ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default UnsecuredLoans;
